import { useLocation } from '@tanstack/react-router';
import React, { useState, useEffect } from 'react';
import expand from '../../assets/expand.svg';
import info from '../../assets/info.svg';
import question from '../../assets/question.svg';
import { useSidebar } from '../../context/SidebarContext';
import Tooltip from '../common/Tooltip';
import menu from './assets/menu.svg';
import notificationImage from '../../assets/notification.svg';
import NotificationPanel from './NotificationPanel';

interface NavbarProps {
  isTourRunning?: boolean;
  currentTourStep?: number;
}

const Navbar: React.FC<NavbarProps> = ({ isTourRunning = false, currentTourStep = -1 }) => {
  const location = useLocation();
  const { isSidebarOpen, toggleSidebar } = useSidebar();
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(6); // This should come from your notifications data
  const [notifications, setNotifications] = useState([]); // This should come from your notifications data

  const getHeading = () => {
    switch (location.pathname) {
      case '/home':
        return 'Home';
      case '/accounts':
        return 'Accounts';
      case '/intent-signals':
        return 'Intent Signals';
      case '/payment':
        return 'Payment';
      default:
        return 'Accounts';
    }
  };

  const getTooltipText = () => {
    switch (location.pathname) {
      case '/accounts':
        return 'Leads generated based on your intent signals';
      case '/intent-signals':
        return 'Indicates user intent or interest in action';
      default:
        return '';
    }
  };

  return (
    <nav className={`fixed bg-[#FFFFFF] border-b h-[49px] flex items-center justify-between z-30 transition-all duration-300 font-inter ${
      isSidebarOpen ? 'left-[262px] w-[calc(100%-262px)]' : 'left-[72px] w-[calc(100%-72px)]'
    }`}>
      <div className="ml-6 flex items-center gap-[6px]">
        {!isSidebarOpen && (
          <button
            onClick={toggleSidebar}
            className="w-[18px] h-[18px] text-[#222222] transition-colors duration-200 hover:border-[1px] hover:rounded-[4px] hover:border-[#E5E5E5] active:text-[#E5E5E5] active:bg-[#222222] active:rounded-[4px] cursor-pointer"
          >
            <img src={expand} alt="expand" className="w-4 h-4 text-[#222222]" />
          </button>
        )}
        <img src={menu} alt="menu" className="w-[16px] h-[16px]" />
        <h1 className="text-[#222222] text-[14px] font-normal leading-[20px] tracking-[-0.28px] pb-[2px]">
          {getHeading()}
        </h1>
        {getTooltipText() && (
          <Tooltip text={getTooltipText()}>
            <img src={info} alt="info" className="w-[14px] h-[14px]" />
          </Tooltip>
        )}
      </div>
      <div className="flex items-center gap-1">
        <div className="relative mr-6">
          <button 
            onClick={() => !isTourRunning && setIsNotificationOpen(!isNotificationOpen)}
            className="notification-button flex items-center gap-1 cursor-pointer rounded-full border border-[#E5E5E5] p-1 hover:bg-[#FAFAFA] transition-colors"
          >
            <img src={notificationImage} alt="Notification" className="w-[14px] h-[14px]" />
          </button>
          {unreadCount > 0 && (
            <div className="absolute -top-[1px] -right-[1px] w-2 h-2 bg-[#29DC5B] rounded-full" />
          )}
          {isNotificationOpen && (
            <div className="notification-panel absolute top-7 -right-4">
              <NotificationPanel 
                notifications={notifications}
                onClose={() => !isTourRunning && setIsNotificationOpen(false)}
              />
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
